import { useState, useEffect, useCallback } from 'react';
import { sb } from '../../lib/supabase';
import { useSupabaseAuth, useAccess } from '../../context/AuthContext';
import Loader from '../../components/Loader';
import {
  PLATFORM_ESTIMATION_DEFAULTS,
  SIZE_GUIDES,
  EXTRAP_ROWS,
  RISK_ROWS,
  getExtrapOverride,
  getOverride,
} from '../../lib/estimationDefaults';

export default function EstimationDefaults() {
  const { profile } = useSupabaseAuth();
  const { isAdmin } = useAccess();
  const orgId = profile?.organisation_id;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [overrides, setOverrides] = useState({});
  const [collapsed, setCollapsed] = useState({});
  const [toast, setToast] = useState(null);

  const showToast = (msg, type = 'error') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const load = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const { data, error } = await sb
        .from('organisations')
        .select('estimation_defaults_json')
        .eq('id', orgId)
        .maybeSingle();
      if (error) throw error;
      setOverrides(data?.estimation_defaults_json || {});
    } catch (e) {
      console.error('[EstimationDefaults] load failed', e);
      showToast('Failed to load estimation defaults');
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => { load(); }, [load]);

  async function persist(next) {
    if (!orgId || !isAdmin) return;
    setSaving(true);
    try {
      const { error } = await sb.from('organisations')
        .update({ estimation_defaults_json: next })
        .eq('id', orgId);
      if (error) throw error;
      setOverrides(next);
      showToast('Defaults saved', 'success');
    } catch (e) {
      console.error('[EstimationDefaults] save failed', e);
      showToast('Failed to save');
    } finally {
      setSaving(false);
    }
  }

  function setOverride(key, value) {
    const next = { ...overrides, [key]: value };
    persist(next);
  }

  function clearOverride(key) {
    const next = { ...overrides };
    delete next[key];
    persist(next);
  }

  function resetAll() {
    persist({});
  }

  function toggleSection(id) {
    setCollapsed((prev) => ({ ...prev, [id]: !prev[id] }));
  }

  if (loading) return <Loader text="Loading estimation defaults…" />;

  if (!isAdmin) {
    return (
      <div className="content">
        <div className="empty" style={{ marginTop: 40 }}>Company admin access required.</div>
      </div>
    );
  }

  const overrideCount = Object.keys(overrides).length;

  return (
    <div className="ed-admin-body">
      <div className="ed-admin-header">
        <div className="ed-admin-title">Estimation defaults</div>
        <div className="ed-admin-subtitle">
          Company defaults apply to all programmes unless overridden at programme level by the PM or SA.
          Platform defaults (blue) reflect SAP S/4HANA best practice via the Platform KB.
        </div>
      </div>

      {overrideCount > 0 && (
        <div className="ed-reset-banner">
          <span>
            <strong>{overrideCount} company override{overrideCount > 1 ? 's' : ''} active</strong>
            {' — '}PMs and SAs can further override at programme level.
          </span>
          <button type="button" className="btn sm danger" disabled={saving} onClick={resetAll}>Reset all to platform defaults</button>
        </div>
      )}

      <div className="ed-section-card">
        <button type="button" className="ed-section-hdr" onClick={() => toggleSection('matrix')}>
          <span className="ed-section-title">Build effort matrix</span>
          <span className="ed-section-meta">
            <span className="ed-platform-badge">SAP S/4HANA Platform KB</span>
            <span>Hours per requirement by size × classification</span>
          </span>
        </button>
        {!collapsed.matrix && (
          <div className="ed-section-body">
            <div className="ed-kb-note">
              The build effort matrix is set by the Platform KB and cannot be overridden at company level.
            </div>
            <table className="ed-matrix-table">
              <thead>
                <tr>
                  <th className="left">Size</th>
                  <th>OOTB</th><th>Config</th><th>Enhancement</th><th>Custom Dev</th>
                  <th className="left guide">Size guidance</th>
                </tr>
              </thead>
              <tbody>
                {['XS', 'S', 'M', 'L', 'XL'].map((sz) => (
                  <tr key={sz}>
                    <td className="size-label">{sz}</td>
                    {['OOTB', 'Config', 'Enhancement', 'Custom'].map((c) => (
                      <td key={c} className="matrix-val">{PLATFORM_ESTIMATION_DEFAULTS.matrix[sz][c]}h</td>
                    ))}
                    <td className="guide">{SIZE_GUIDES[sz]}</td>
                  </tr>
                ))}
                <tr className="modifier-row">
                  <td className="size-label muted">L4 modifier</td>
                  <td colSpan={4} className="matrix-val">× {PLATFORM_ESTIMATION_DEFAULTS.l4mult} applied to L4 requirements</td>
                  <td />
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="ed-section-card">
        <button type="button" className="ed-section-hdr" onClick={() => toggleSection('extrap')}>
          <span className="ed-section-title">Non-build extrapolation</span>
          <span className="ed-section-meta">% of total build effort</span>
        </button>
        {!collapsed.extrap && (
          <div className="ed-section-body flush">
            {EXTRAP_ROWS.map((row) => {
              const val = getExtrapOverride(overrides, row.key);
              const key = `extrap_${row.key}`;
              const overridden = overrides[key] !== undefined;
              return (
                <div key={row.key} className="ed-extrap-row">
                  <div className="ed-extrap-label">
                    <div className="ed-extrap-name">{row.name}</div>
                    <div className="ed-extrap-desc">{row.desc}</div>
                  </div>
                  <div className="ed-extrap-values">
                    <span className="ed-platform-val">{PLATFORM_ESTIMATION_DEFAULTS.extrap[row.key]}%</span>
                    <input
                      type="number"
                      className={`ed-extrap-input${overridden ? ' overridden' : ''}`}
                      value={val}
                      disabled={saving}
                      onChange={(e) => setOverride(key, Number(e.target.value))}
                    />
                    <span className="ed-unit">%</span>
                    {overridden && (
                      <button type="button" className="ed-reset-btn" onClick={() => clearOverride(key)}>Reset</button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="ed-section-card">
        <button type="button" className="ed-section-hdr" onClick={() => toggleSection('risk')}>
          <span className="ed-section-title">Risk multipliers</span>
          <span className="ed-section-meta">PERT-style programme factors</span>
        </button>
        {!collapsed.risk && (
          <div className="ed-section-body flush">
            {RISK_ROWS.map((row) => {
              const plat = PLATFORM_ESTIMATION_DEFAULTS.risk[row.key];
              return (
                <div key={row.key} className="ed-risk-row">
                  <div className="ed-risk-label">
                    <div className="ed-risk-name">{row.name}</div>
                    <div className="ed-risk-desc">{row.desc}</div>
                  </div>
                  <div className="ed-risk-range">
                    <span className="ed-range-label">{plat.min}</span>
                    <span className="ed-range-sep">—</span>
                    <span className="ed-range-val">{plat.default}</span>
                    <span className="ed-range-sep">—</span>
                    <span className="ed-range-label">{plat.max}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="ed-section-card">
        <button type="button" className="ed-section-hdr" onClick={() => toggleSection('cost')}>
          <span className="ed-section-title">Cost parameters</span>
        </button>
        {!collapsed.cost && (
          <div className="ed-section-body flush">
            {[
              { key: 'dayRate', name: 'SI blended day rate', unit: '£/day', platform: PLATFORM_ESTIMATION_DEFAULTS.dayRate },
              { key: 'hrsPerDay', name: 'Hours per day', unit: 'h', platform: PLATFORM_ESTIMATION_DEFAULTS.hrsPerDay },
              { key: 'contingency', name: 'Contingency', unit: '%', platform: PLATFORM_ESTIMATION_DEFAULTS.contingency },
            ].map((row) => {
              const val = getOverride(overrides, row.key, row.platform);
              const overridden = overrides[row.key] !== undefined;
              return (
                <div key={row.key} className="ed-cost-row">
                  <div className="ed-cost-label">
                    <div className="ed-cost-name">{row.name}</div>
                  </div>
                  <div className="ed-cost-right">
                    <span className="ed-platform-val">{row.platform}{row.unit === '%' ? '%' : ''}</span>
                    <input
                      type="number"
                      className={`ed-cost-input${overridden ? ' overridden' : ''}`}
                      value={val}
                      disabled={saving}
                      onChange={(e) => setOverride(row.key, Number(e.target.value))}
                    />
                    <span className="ed-unit">{row.unit}</span>
                    {overridden && (
                      <button type="button" className="ed-reset-btn" onClick={() => clearOverride(row.key)}>Reset</button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {toast && <div className={`toast ${toast.type === 'success' ? 'toast-success' : 'toast-error'}`}>{toast.msg}</div>}
    </div>
  );
}
